import type React from "react";
import { useEffect, useMemo, useState } from "react";

import { styled } from "styled-components";

import { Loader, Message, Segment } from "simplydo/react/ui";

const VideoDisplayer = styled.div<{ $canExpand: boolean }>`
  display: flex;
  align-items: center;
  width: auto;
  text-align: left;
  overflow: hidden;

  .video-embedded iframe,
  .video-uploaded video {
    position: relative;
    max-height: 350px;
    transition: max-height 0.5s;
    -webkit-transition: max-height 0.5s;

    ${({ $canExpand }) =>
      $canExpand ?
        `
      &:hover {
        max-height: min(80vh, 600px);
      }
    `
      : ""}
  }

  .video-uploaded,
  .video-embedded {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;

    max-width: 100%;

    video,
    iframe {
      max-width: 100%;
    }
  }
`;

type VideoPlayerProps = {
  src: string;
  force?: boolean;
  canExpand?: boolean;
  style?: React.CSSProperties;
};

const VideoEmbed = ({ src }: { src: string }) => {
  return (
    <div className="video-embedded">
      <iframe
        title="Video"
        style={{ height: 350, width: "100%" }}
        src={src.replace("embed-", "")}
        frameBorder="0"
        allow="autoplay; encrypted-media"
        allowFullScreen
      ></iframe>
    </div>
  );
};

const VideoUploaded = ({ src }: { src: string }) => {
  const [headersLoading, setHeadersLoading] = useState(true);
  const [videoHeaders, setVideoHeaders] = useState<Record<string, string>>();
  const [devOverride, setDevOverride] = useState(false);

  const isConverted =
    videoHeaders?.["x-amz-meta-video-converted"] === "true" ||
    !!videoHeaders?.["x-amz-meta-mediaconvert-jobid"];

  useEffect(() => {
    setHeadersLoading(true);
    let isMounted = true;

    let isDone = false;
    const fetchHeaders = () => {
      fetch(src, { method: "HEAD" })
        .then((response) => {
          if (!isMounted) return;

          const headers: Record<string, string> = {};
          response.headers.forEach((value, key) => {
            headers[key] = value;
          });
          setVideoHeaders(headers);
          setHeadersLoading(false);

          if (
            headers["x-amz-meta-video-converted"] === "true" ||
            !!headers["x-amz-meta-mediaconvert-jobid"]
          ) {
            isDone = true;
          }
        })
        .catch((error) => {
          if (!isMounted) return;
          setHeadersLoading(false);
          console.error("Error fetching video:", error);
        });
    };
    fetchHeaders();

    if (!isDone) {
      const interval = setInterval(() => {
        fetchHeaders();
        if (isDone) {
          clearInterval(interval);
        }
      }, 3000);

      const stopTimeout = setTimeout(
        () => {
          clearInterval(interval);
        },
        60 * 1000 * 5,
      );
      return () => {
        clearInterval(interval);
        clearTimeout(stopTimeout);
        isMounted = false;
      };
    }

    return () => {
      isMounted = false;
    };
  }, [src]);

  const canPlay = useMemo(() => {
    if (!src) return false;
    const v = src.toLowerCase();
    let type = "video/";
    if (v.indexOf(".mp4") > 0) type += "mp4";
    else if (v.indexOf(".avi") > 0) type += "avi";
    else type += "mp4";
    const videoElement = document.createElement("video");
    const canPlay = !!videoElement.canPlayType(type);
    videoElement.remove();
    return canPlay;
  }, [src]);

  if (headersLoading) {
    return (
      <Segment
        secondary
        style={{
          height: 350,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          width: 600,
        }}
      >
        <Message icon style={{ maxWidth: 350 }}>
          <Loader active inline="centered" style={{ marginRight: 20 }} />
          <Message.Content>
            <Message.Header>Preparing video...</Message.Header>
            <p>One moment while we get this video ready.</p>
          </Message.Content>
        </Message>
      </Segment>
    );
  }

  if (!isConverted && !devOverride) {
    return (
      <Segment
        secondary
        style={{
          height: 350,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          width: 600,
        }}
      >
        <Message icon style={{ maxWidth: 350 }}>
          <Loader active inline="centered" style={{ marginRight: 20 }} />
          <Message.Content>
            <Message.Header>We are processing this video</Message.Header>
            <p>Please save your changes and check back soon.</p>
            {import.meta.env.VITE_STAGE === "dev" ?
              <p>
                <br />
                <strong>Dev mode:</strong> You can force set the source to the
                original video with the button below.
                <br />
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    setDevOverride(true);
                  }}
                >
                  Force source
                </a>
              </p>
            : null}
          </Message.Content>
        </Message>
      </Segment>
    );
  }

  if (!canPlay) {
    return (
      <Segment
        secondary
        style={{
          height: 350,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          width: 600,
        }}
      >
        <Message icon style={{ maxWidth: 350 }}>
          <Message.Content>
            <Message.Header>Video not supported</Message.Header>
            <p>This video format is not supported by your browser.</p>
          </Message.Content>
        </Message>
      </Segment>
    );
  }

  return (
    <div className="video-uploaded">
      <video key={src} controls preload="metadata">
        <source src={src} type="video/mp4" />
        Your browser does not support this video.
      </video>
    </div>
  );
};

export const VideoPlayer = ({ src, canExpand, style }: VideoPlayerProps) => {
  if (!src) return null;
  return (
    <VideoDisplayer style={style} $canExpand={canExpand ?? false}>
      {src.indexOf("embed-") === 0 ?
        <VideoEmbed src={src} />
      : <VideoUploaded src={src} />}
    </VideoDisplayer>
  );
};
